import React, { createContext, useState, useContext, useCallback } from "react";
import PropTypes from "prop-types";
import { loginRequest, logoutRequest, checkLoggedIn } from "services/api";
import EmployeeModel from "models/employeeModel";
import { getCompanyById } from "services/api";
import { getCompanies } from "services/api";
import { getIsTopManagement } from "services/api";
import { getEmployeeModules } from "services/api";
import { getActiveModules } from "services/api";
import { getTicketsForEmployee,fetchCeo } from "services/api";

import { is } from "date-fns/locale";
export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false); // Replace with real authentication check
  const [viewAsAdmin, setViewAsAdmin] = useState(false);
  const [employee, setEmployee] = useState();
  const [jobs, setJobs] = useState();
  const [companies, setCompanies] = useState();
  const [ceoData,setCeoData] = useState();
  const [allEmps, setAllEmps] = useState([]);
  const [companyModules, setCompanyModules] = useState([]);
  const [jobToBranch, setJobToBranch] = useState();
  const [areas, setAreas] = useState();
  const [isTopManagement, setIsTopManagement] = useState(false);
  const [chats, setChats] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [chatschanged, setChatsChanged] = useState(false);
  const [modules, setModules] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [quarter, setQuarter] = useState(Math.floor((new Date().getMonth() + 3) / 3));
  const [isQuarterly, setIsQuarterly] = useState(false);
  // ✅ Common function to fetch company, top management, and modules
  const fetchUserRelatedData = async (emp) => {
    const promises = [getCompanies(), getIsTopManagement(emp.id), getActiveModules(), fetchCeo()];
    // Only fetch modules if admin
    if (emp.isAdmin) promises.push(getEmployeeModules(emp.id));

    const [compRes, topRes, activeMods, ceoRes, modsRes] = await Promise.all(promises);

    if (compRes !== "error") setCompanies(compRes);
    if (topRes !== "error") setIsTopManagement(topRes);
    if (activeMods !== "error") setCompanyModules(activeMods);
    if (ceoRes !== "error") setCeoData(ceoRes);

    if (emp.isAdmin && modsRes !== "error") {
      const mappedAccess = modsRes.reduce((acc, curr) => {
        acc[curr.moduleName] = curr.accessLevel;
        return acc;
      }, {});
      setModules(mappedAccess);
    }

    const tickets = await getTicketsForEmployee();
    if (tickets !== "error") setTickets(tickets);

    // Set admin view if role != 1
    if (emp.isAdmin && emp.role !== 1) setViewAsAdmin(true);
  };

  // ➕ Login function
  const login = async (id, password) => {
    const response = await loginRequest(id, password);

    if (response instanceof EmployeeModel) {
      setEmployee(response);
      await fetchUserRelatedData(response);
      setIsAuthenticated(true);
      return "Done";
    }

    return response;
  };

  // 🔁 Check logged-in status (on app mount)
  const checkLoggedInStatus = async () => {
    const response = await checkLoggedIn();

    if (response instanceof EmployeeModel) {
      setEmployee(response);
      await fetchUserRelatedData(response);
      setIsAuthenticated(true);
      return "Done";
    }

    return response;
  };

  // 🚪 Logout
  const logout = useCallback(() => {
    setEmployee(null);
    setJobs();
    setCompanies();
    setJobToBranch();
    setChats([]);
    setUnreadCount(0);
    setChatsChanged(false);
    setIsAuthenticated(false);
    setIsTopManagement(false);
    logoutRequest();
    localStorage.removeItem("isSuperAdminSession");
    localStorage.removeItem("originalToken");
  }, []);

  // 🔄 Switch view (admin <-> user)
  const switchView = () => {
    if (employee?.role === 1) setViewAsAdmin((prev) => !prev);
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        login,
        logout,
        employee,
        setEmployee,
        checkLoggedInStatus,
        switchView,
        viewAsAdmin,
        jobs,
        setJobs,
        companies,
        setCompanies,
        ceoData,
        allEmps,
        setAllEmps,
        jobToBranch,
        setJobToBranch,
        chats,
        setChats,
        unreadCount,
        setUnreadCount,
        chatschanged,
        setChatsChanged,
        selectedMonth,
        setSelectedMonth,
        quarter,
        setQuarter,
        isQuarterly,
        setIsQuarterly,
        selectedYear,
        setSelectedYear,
        areas,
        setAreas,
        isTopManagement,
        modules,
        companyModules,
        tickets,
        setTickets,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
AuthProvider.propTypes = {
  children: PropTypes.node.isRequired, // Validate that children is a React node and required
};
export const useAuth = () => useContext(AuthContext);
